import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Plus, Edit3, Copy, Trash2, X, Check, Star, GripVertical, Palette,
  RefreshCw, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';

const INITIAL_PLAN = {
  name: { en: '', ar: '' },
  description: { en: '', ar: '' },
  slug: '',
  monthlyPrice: 0,
  yearlyPrice: 0,
  maxBranches: 1,
  maxUsers: 5,
  maxAIAnalysisPerMonth: 0,
  additionalBranchPricePercent: 0,
  maxSubscribers: 0,
  features: {},
  highlightPoints: [],
  allowedPages: [],
  color: '#3B82F6',
  isPopular: false,
  sortOrder: 0,
  trialDays: 14,
  // Per-addon pricing (Phase 1: custom domain)
  addonPrices: {
    customDomain: { enabled: false, monthly: 0, yearly: 0 },
  },
};

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({ ...INITIAL_PLAN });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, featuresRes] = await Promise.all([
        superAdminAPI.getPlans(),
        superAdminAPI.getFeatures(),
      ]);
      setPlans(plansRes.data?.data || plansRes.data || []);
      setFeatures(featuresRes.data?.data || featuresRes.data || []);
    } catch (error) {
      toast.error('فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingPlan(null);
    setFormData({ ...INITIAL_PLAN });
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    // Convert backend features array [{ key, enabled }] to frontend object { key: bool }
    const featuresObj = {};
    if (Array.isArray(plan.features)) {
      plan.features.forEach((f) => { featuresObj[f.key] = !!f.enabled; });
    } else if (plan.features && typeof plan.features === 'object') {
      Object.assign(featuresObj, plan.features);
    }
    setFormData({
      name: { en: plan.name || '', ar: plan.nameAr || '' },
      description: { en: plan.description || '', ar: plan.descriptionAr || '' },
      slug: plan.slug || '',
      monthlyPrice: plan.monthlyPrice || 0,
      yearlyPrice: plan.yearlyPrice || 0,
      maxBranches: plan.limits?.maxBranches || 1,
      maxUsers: plan.limits?.maxUsers || 5,
      maxAIAnalysisPerMonth: plan.limits?.maxAIAnalysisPerMonth ?? 0,
      additionalBranchPricePercent: plan.limits?.additionalBranchPricePercent || 0,
      maxSubscribers: plan.maxSubscribers || 0,
      features: featuresObj,
      highlightPoints: plan.highlightPoints || [],
      allowedPages: plan.allowedPages || [],
      color: plan.color || '#3B82F6',
      isPopular: plan.isPopular || false,
      sortOrder: plan.sortOrder || 0,
      trialDays: plan.trialDays || 14,
      addonPrices: {
        customDomain: {
          enabled: !!plan.addonPrices?.customDomain?.enabled,
          monthly: plan.addonPrices?.customDomain?.monthly || 0,
          yearly: plan.addonPrices?.customDomain?.yearly || 0,
        },
      },
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name?.ar && !formData.name?.en) {
      toast.error('يرجى إدخال اسم الباقة');
      return;
    }
    setSaving(true);
    try {
      // Transform frontend shape to match backend Plan model
      const payload = {
        name: formData.name?.en || formData.name?.ar || '',
        nameAr: formData.name?.ar || formData.name?.en || '',
        description: formData.description?.en || '',
        descriptionAr: formData.description?.ar || '',
        slug: formData.slug,
        monthlyPrice: formData.monthlyPrice,
        yearlyPrice: formData.yearlyPrice,
        limits: {
          maxBranches: formData.maxBranches,
          maxUsers: formData.maxUsers,
          maxAIAnalysisPerMonth: formData.maxAIAnalysisPerMonth,
          additionalBranchPricePercent: formData.additionalBranchPricePercent,
        },
        maxSubscribers: formData.maxSubscribers || 0,
        features: Object.entries(formData.features || {}).map(([key, enabled]) => ({ key, enabled: !!enabled })),
        highlightPoints: (formData.highlightPoints || []).filter(p => p.text?.trim()),
        allowedPages: formData.allowedPages,
        color: formData.color,
        isPopular: formData.isPopular,
        sortOrder: formData.sortOrder,
        trialDays: formData.trialDays,
        addonPrices: {
          customDomain: {
            enabled: !!formData.addonPrices?.customDomain?.enabled,
            monthly: Number(formData.addonPrices?.customDomain?.monthly) || 0,
            yearly: Number(formData.addonPrices?.customDomain?.yearly) || 0,
          },
        },
      };
      if (editingPlan) {
        await superAdminAPI.updatePlan(editingPlan._id, payload);
        toast.success('تم تحديث الباقة');
      } else {
        await superAdminAPI.createPlan(payload);
        toast.success('تم إنشاء الباقة');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في حفظ الباقة');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (plan) => {
    try {
      await superAdminAPI.duplicatePlan(plan._id, {
        name: (plan.name || '') + ' (Copy)',
        nameAr: (plan.nameAr || '') + ' (نسخة)',
      });
      toast.success('تم نسخ الباقة');
      fetchData();
    } catch (error) {
      toast.error('فشل في نسخ الباقة');
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`هل أنت متأكد من حذف باقة "${plan.nameAr || plan.name}"؟`)) return;
    try {
      await superAdminAPI.deletePlan(plan._id);
      toast.success('تم حذف الباقة');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في حذف الباقة');
    }
  };

  const handleSeedFeatures = async () => {
    setSeeding(true);
    try {
      const res = await superAdminAPI.seedFeatures();
      const result = res.data?.data;
      toast.success(`تم بنجاح: ${result?.total || 0} ميزة متاحة الآن`);
      fetchData(); // Reload to show new features
    } catch (error) {
      toast.error('فشل في تهيئة المميزات');
    } finally {
      setSeeding(false);
    }
  };

  const toggleFeature = (featureKey) => {
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [featureKey]: !prev.features[featureKey],
      },
    }));
  };

  const togglePage = (page) => {
    setFormData((prev) => {
      const pages = prev.allowedPages || [];
      return {
        ...prev,
        allowedPages: pages.includes(page) ? pages.filter((p) => p !== page) : [...pages, page],
      };
    });
  };

  const AVAILABLE_PAGES = [
    'dashboard', 'bookings', 'customers', 'tests', 'lab_results',
    'reports', 'marketing', 'settings', 'qc', 'staff_management',
    'branches', 'devices', 'contractor_bookings',
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-10 w-32 bg-gray-200 rounded-xl animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 animate-pulse space-y-4">
              <div className="h-6 w-24 bg-gray-200 rounded" />
              <div className="h-4 w-32 bg-gray-200 rounded" />
              <div className="space-y-2">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="h-3 bg-gray-200 rounded w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">الباقات</h1>
          <p className="text-sm text-gray-500 mt-1">إدارة باقات الاشتراك</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          إنشاء باقة
        </button>
      </div>

      {/* Features Empty Banner */}
      {features.length === 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">لا توجد ميزات محددة في النظام</p>
            <p className="text-xs text-amber-700 mt-1">
              المميزات هي العناصر التي تُضاف للباقات (QC، شات المختبر، ربط الأجهزة، ZATCA...). اضغط على الزر لتهيئة جميع المميزات المتاحة.
            </p>
          </div>
          <button
            onClick={handleSeedFeatures}
            disabled={seeding}
            className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-60 flex-shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${seeding ? 'animate-spin' : ''}`} />
            {seeding ? 'جاري...' : 'تهيئة المميزات'}
          </button>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan, i) => (
          <motion.div
            key={plan._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className={`bg-white rounded-2xl shadow-sm border-2 overflow-hidden relative ${
              plan.isPopular ? 'border-blue-500' : 'border-gray-100'
            }`}
          >
            {plan.isPopular && (
              <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                <Star className="w-3 h-3" /> الأكثر طلبا
              </div>
            )}

            <div className="p-6">
              {/* Color Bar */}
              <div
                className="w-full h-1.5 rounded-full mb-4"
                style={{ backgroundColor: plan.color || '#3B82F6' }}
              />

              <h3 className="text-lg font-bold text-gray-800 mb-1">
                {plan.nameAr || plan.name || '-'}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {plan.descriptionAr || plan.description || ''}
              </p>

              {/* Pricing */}
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-bold text-gray-800">
                  {plan.monthlyPrice?.toLocaleString() || 0}
                </span>
                <span className="text-sm text-gray-500">ر.س / شهر</span>
              </div>
              {plan.yearlyPrice > 0 && (
                <p className="text-xs text-gray-400 -mt-3 mb-4">
                  {plan.yearlyPrice?.toLocaleString()} ر.س / سنة
                </p>
              )}

              {/* Limits */}
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-center justify-between text-gray-600">
                  <span>الفروع</span>
                  <span className="font-medium">{plan.limits?.maxBranches === -1 ? '∞' : plan.limits?.maxBranches || '-'}</span>
                </div>
                {plan.limits?.additionalBranchPricePercent > 0 && (
                  <div className="flex items-center justify-between text-gray-500 text-xs">
                    <span>الفرع الإضافي</span>
                    <span className="font-medium text-blue-600">{plan.limits.additionalBranchPricePercent}% من الاشتراك</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-gray-600">
                  <span>المستخدمين</span>
                  <span className="font-medium">{plan.limits?.maxUsers === -1 ? '∞' : plan.limits?.maxUsers || '-'}</span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span>رصيد AI شهري</span>
                  <span className="font-medium">{plan.limits?.maxAIAnalysisPerMonth === -1 ? '∞' : plan.limits?.maxAIAnalysisPerMonth === 0 ? 'معطل' : plan.limits?.maxAIAnalysisPerMonth || 'معطل'}</span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span>أيام التجربة</span>
                  <span className="font-medium">{plan.trialDays || 0}</span>
                </div>
                {plan.maxSubscribers > 0 && (
                  <div className="flex items-center justify-between text-gray-600">
                    <span>حد المشتركين</span>
                    <span className="font-medium text-orange-600">{plan.currentSubscribers || 0}/{plan.maxSubscribers}</span>
                  </div>
                )}
              </div>

              {/* Highlight Points */}
              {Array.isArray(plan.highlightPoints) && plan.highlightPoints.length > 0 && (
                <div className="border-t border-gray-100 pt-3 mb-4">
                  <p className="text-xs text-gray-400 mb-2">نقاط المميزات</p>
                  <div className="space-y-1">
                    {plan.highlightPoints.map((p, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs">
                        <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{p.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
              {Array.isArray(plan.features) && plan.features.length > 0 && (
                <div className="border-t border-gray-100 pt-3 mb-4">
                  <p className="text-xs text-gray-400 mb-2">الميزات</p>
                  <div className="space-y-1">
                    {plan.features.slice(0, 5).map((f) => (
                      <div key={f.key} className="flex items-center gap-2 text-xs">
                        <Check className={`w-3.5 h-3.5 ${f.enabled ? 'text-green-500' : 'text-gray-300'}`} />
                        <span className={f.enabled ? 'text-gray-700' : 'text-gray-400 line-through'}>{f.key}</span>
                      </div>
                    ))}
                    {plan.features.length > 5 && (
                      <p className="text-xs text-gray-400">+{plan.features.length - 5} ميزة أخرى</p>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => openEdit(plan)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200 transition-colors"
                >
                  <Edit3 className="w-4 h-4" /> تعديل
                </button>
                <button
                  onClick={() => handleDuplicate(plan)}
                  className="p-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                  title="نسخ"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(plan)}
                  className="p-2 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-colors"
                  title="حذف"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-16">
          <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">لا توجد باقات</p>
          <button
            onClick={openCreate}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700"
          >
            إنشاء أول باقة
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800">
                  {editingPlan ? 'تعديل الباقة' : 'إنشاء باقة جديدة'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Names */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الاسم (عربي)</label>
                    <input
                      type="text"
                      value={formData.name?.ar || ''}
                      onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ar: e.target.value } })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الاسم (إنجليزي)</label>
                    <input
                      type="text"
                      value={formData.name?.en || ''}
                      onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Descriptions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الوصف (عربي)</label>
                    <textarea
                      value={formData.description?.ar || ''}
                      onChange={(e) => setFormData({ ...formData, description: { ...formData.description, ar: e.target.value } })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الوصف (إنجليزي)</label>
                    <textarea
                      value={formData.description?.en || ''}
                      onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الرابط (Slug)</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="basic-plan"
                    dir="ltr"
                  />
                </div>

                {/* Pricing */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">السعر الشهري (ر.س)</label>
                    <input
                      type="number"
                      value={formData.monthlyPrice}
                      onChange={(e) => setFormData({ ...formData, monthlyPrice: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">السعر السنوي (ر.س)</label>
                    <input
                      type="number"
                      value={formData.yearlyPrice}
                      onChange={(e) => setFormData({ ...formData, yearlyPrice: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={0}
                    />
                  </div>
                </div>

                {/* Limits */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">حد الفروع</label>
                    <input
                      type="number"
                      value={formData.maxBranches}
                      onChange={(e) => setFormData({ ...formData, maxBranches: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={1}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">حد المستخدمين</label>
                    <input
                      type="number"
                      value={formData.maxUsers}
                      onChange={(e) => setFormData({ ...formData, maxUsers: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={1}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">رصيد AI شهري</label>
                    <input
                      type="number"
                      value={formData.maxAIAnalysisPerMonth}
                      onChange={(e) => setFormData({ ...formData, maxAIAnalysisPerMonth: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={-1}
                    />
                    <p className="text-xs text-gray-400 mt-1">0 = معطل | -1 = غير محدود | رقم = الحد الشهري</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">% سعر فرع إضافي</label>
                    <input
                      type="number"
                      value={formData.additionalBranchPricePercent}
                      onChange={(e) => setFormData({ ...formData, additionalBranchPricePercent: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={0}
                    />
                  </div>
                </div>

                {/* Display Settings */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">اللون</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        dir="ltr"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ترتيب العرض</label>
                    <input
                      type="number"
                      value={formData.sortOrder}
                      onChange={(e) => setFormData({ ...formData, sortOrder: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">أيام التجربة</label>
                    <input
                      type="number"
                      value={formData.trialDays}
                      onChange={(e) => setFormData({ ...formData, trialDays: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={0}
                    />
                  </div>
                </div>

                {/* isPopular + maxSubscribers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                  <label className="flex items-center gap-3 cursor-pointer py-2">
                    <input
                      type="checkbox"
                      checked={formData.isPopular}
                      onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">الأكثر طلبا (Popular)</span>
                  </label>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">حد المشتركين (0 = غير محدود)</label>
                    <input
                      type="number"
                      value={formData.maxSubscribers}
                      onChange={(e) => setFormData({ ...formData, maxSubscribers: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={0}
                      placeholder="0 = غير محدود"
                    />
                  </div>
                </div>

                {/* Add-on Pricing */}
                <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/40">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-bold text-gray-800">تسعير الإضافات (Add-ons)</h4>
                      <p className="text-xs text-gray-500 mt-0.5">يمكن للمختبرات شراء هذه الإضافات دون ترقية الباقة.</p>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-gray-800">الدومين المخصص (Custom Domain)</div>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-gray-500">متاح كإضافة</span>
                        <input
                          type="checkbox"
                          checked={!!formData.addonPrices?.customDomain?.enabled}
                          onChange={(e) => setFormData({
                            ...formData,
                            addonPrices: {
                              ...(formData.addonPrices || {}),
                              customDomain: {
                                ...(formData.addonPrices?.customDomain || {}),
                                enabled: e.target.checked,
                              },
                            },
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </label>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">السعر الشهري (ر.س)</label>
                        <input
                          type="number"
                          min={0}
                          disabled={!formData.addonPrices?.customDomain?.enabled}
                          value={formData.addonPrices?.customDomain?.monthly ?? 0}
                          onChange={(e) => setFormData({
                            ...formData,
                            addonPrices: {
                              ...(formData.addonPrices || {}),
                              customDomain: {
                                ...(formData.addonPrices?.customDomain || {}),
                                monthly: Number(e.target.value),
                              },
                            },
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">السعر السنوي (ر.س)</label>
                        <input
                          type="number"
                          min={0}
                          disabled={!formData.addonPrices?.customDomain?.enabled}
                          value={formData.addonPrices?.customDomain?.yearly ?? 0}
                          onChange={(e) => setFormData({
                            ...formData,
                            addonPrices: {
                              ...(formData.addonPrices || {}),
                              customDomain: {
                                ...(formData.addonPrices?.customDomain || {}),
                                yearly: Number(e.target.value),
                              },
                            },
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      اترك السعر = 0 أو ألغِ التفعيل لإخفاء الإضافة من صفحة "شراء الإضافة" للمختبرات على هذه الباقة.
                    </p>
                  </div>
                </div>

                {/* Highlight Points */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">نقاط المميزات (تظهر في بطاقة الباقة)</label>
                  <div className="space-y-2">
                    {(formData.highlightPoints || []).map((point, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-5 text-center flex-shrink-0">{idx + 1}</span>
                        <input
                          type="text"
                          value={point.text || ''}
                          onChange={(e) => {
                            const updated = [...(formData.highlightPoints || [])];
                            updated[idx] = { ...updated[idx], text: e.target.value };
                            setFormData({ ...formData, highlightPoints: updated });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="مثال: دعم فني على مدار الساعة"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (formData.highlightPoints || []).filter((_, i) => i !== idx);
                            setFormData({ ...formData, highlightPoints: updated });
                          }}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          highlightPoints: [...(formData.highlightPoints || []), { text: '', textEn: '' }],
                        });
                      }}
                      className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
                    >
                      <Plus className="w-4 h-4" />
                      إضافة نقطة
                    </button>
                  </div>
                </div>

                {/* Features */}
                {features.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">الميزات</label>
                    <div className="border border-gray-200 rounded-xl p-4 max-h-48 overflow-y-auto space-y-2">
                      {features.map((feature) => {
                        const key = feature.key || feature.slug || feature._id;
                        return (
                          <label key={key} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1.5 rounded-lg">
                            <input
                              type="checkbox"
                              checked={!!formData.features[key]}
                              onChange={() => toggleFeature(key)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                              <span className="text-sm text-gray-700">{feature.labelAr || feature.label || key}</span>
                              {feature.descriptionAr && (
                                <p className="text-xs text-gray-400">{feature.descriptionAr}</p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Allowed Pages */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الصفحات المسموحة</label>
                  <div className="border border-gray-200 rounded-xl p-4 max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                      {AVAILABLE_PAGES.map((page) => (
                        <label key={page} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(formData.allowedPages || []).includes(page)}
                            onChange={() => togglePage(page)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{page}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'جاري الحفظ...' : editingPlan ? 'تحديث الباقة' : 'إنشاء الباقة'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlanManagement;
